import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import {
  Gift,
  Copy,
  CheckCircle,
  ShareNetwork,
  WhatsappLogo,
  TwitterLogo,
  TelegramLogo,
  FacebookLogo,
  YoutubeLogo,
  Coins,
  Wallet,
  ArrowLeft,
  Sparkle,
  ShieldWarning,
} from "@phosphor-icons/react";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : "—");

export default function ReferralPage() {
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [s, r, w] = await Promise.all([
        api.get("/referral/me"),
        api.get("/referral/list"),
        api.get("/wallet"),
      ]);
      setStats(s.data);
      setReferrals(r.data);
      setWallet(w.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Referral load failed:", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const referralLink = useMemo(() => {
    if (!stats?.referral_code) return "";
    return `${window.location.origin}/register?ref=${stats.referral_code}`;
  }, [stats]);

  const shareText = useMemo(() => {
    if (!stats?.referral_code) return "";
    return `🚀 Got tired of missing breakouts on NSE? Try Breakout Scanner — multi-year breakouts with Entry/SL/Target auto-calculated. Sign up with my code ${stats.referral_code} and get 5 bonus credits! ${referralLink}`;
  }, [stats, referralLink]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Clipboard failed:", e);
    }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, "_blank");
  const shareTelegram = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`, "_blank");
  // Facebook sharer only accepts a URL — it pulls the site's OpenGraph metadata
  // (title, image, description) to render the preview card, so we don't need
  // to pass the long share text.
  const shareFacebook = () => window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
    "_blank",
  );
  // YouTube has no public share-intent URL (you can only share YouTube
  // *videos*, not external links). Instead we copy the share text to the
  // clipboard so the user can paste it into a YouTube community post or
  // comment in one click.
  const shareYoutube = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      window.open("https://studio.youtube.com/", "_blank");
    } catch (e) {
      console.error("YouTube share clipboard failed:", e);
      window.open("https://studio.youtube.com/", "_blank");
    }
  };

  if (!stats) {
    return (
      <div className="min-h-screen bg-app text-primary-fg flex items-center justify-center">
        <div className="font-mono text-xs text-muted-fg uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  const balance = wallet?.balance || 0;
  const completed = stats.completed_referrals;
  const pending = stats.pending_referrals;
  const totalEarned = completed * stats.rewards_per_referral;
  const cashableRupees = Math.floor(balance / stats.credits_per_rupee);

  return (
    <div className="min-h-screen bg-app text-primary-fg" data-testid="referral-page">
      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-6"
          data-testid="back-to-dash"
        >
          <ArrowLeft size={11} weight="bold" /> back to dashboard
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-mod flex items-center gap-2 mb-2">
            <Sparkle size={10} weight="fill" /> Refer & Earn
          </div>
          <h1 className="text-3xl md:text-4xl font-mono text-primary-fg tracking-tight mb-2">
            Invite friends. Earn real cashback.
          </h1>
          <p className="text-sm text-secondary-fg max-w-2xl">
            Share your unique link. Friend gets <b className="text-pos">5 bonus credits</b> on signup.
            You earn <b className="text-pos">10 credits</b> the moment they upgrade.
            Redeem 50 credits = ₹50 cashback.
          </p>
        </motion.div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat icon={<Wallet size={14} weight="bold" />} label="Wallet" value={`${fmt(balance)} cr`} sub={`≈ ₹${fmt(cashableRupees)}`} accent="text-pos" testid="stat-wallet" />
          <Stat icon={<Coins size={14} weight="bold" />} label="Total Earned" value={`${fmt(totalEarned)} cr`} sub={`from ${completed} paid`} testid="stat-earned" />
          <Stat icon={<Gift size={14} weight="bold" />} label="Completed" value={completed} sub="paid signups" accent="text-pos" testid="stat-completed" />
          <Stat icon={<ShieldWarning size={14} weight="bold" />} label="Pending" value={pending} sub={`${fmt(pending * stats.rewards_per_referral)} cr to unlock`} accent="text-mod" testid="stat-pending" />
        </div>

        {/* Share card */}
        <div className="border border-subtle bg-surface rounded-sm p-5 md:p-6 mb-8" data-testid="share-card">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg mb-3">/// your referral link</div>
          <div className="flex flex-col md:flex-row gap-2 mb-4">
            <input
              readOnly
              value={referralLink}
              data-testid="referral-link-input"
              className="flex-1 px-3 py-3 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm tabular-nums focus:border-focus outline-none"
              onClick={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="px-4 py-3 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:opacity-90 transition flex items-center justify-center gap-2"
              style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
            >
              {copied ? <><CheckCircle size={12} weight="fill" /> Copied!</> : <><Copy size={12} weight="bold" /> Copy</>}
            </button>
          </div>

          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">share via</span>
            <button type="button" onClick={shareWhatsApp} data-testid="share-whatsapp" className="px-3 py-2 border border-subtle rounded-sm hover:bg-surface-hover transition flex items-center gap-1.5 text-[11px] font-mono">
              <WhatsappLogo size={14} weight="fill" className="text-pos" /> WhatsApp
            </button>
            <button type="button" onClick={shareTwitter} data-testid="share-twitter" className="px-3 py-2 border border-subtle rounded-sm hover:bg-surface-hover transition flex items-center gap-1.5 text-[11px] font-mono">
              <TwitterLogo size={14} weight="fill" /> X / Twitter
            </button>
            <button type="button" onClick={shareTelegram} data-testid="share-telegram" className="px-3 py-2 border border-subtle rounded-sm hover:bg-surface-hover transition flex items-center gap-1.5 text-[11px] font-mono">
              <TelegramLogo size={14} weight="fill" className="text-mod" /> Telegram
            </button>
            <button
              type="button"
              onClick={shareFacebook}
              data-testid="share-facebook"
              title="Share your referral link on Facebook"
              className="px-3 py-2 border border-subtle rounded-sm hover:bg-surface-hover transition flex items-center gap-1.5 text-[11px] font-mono"
            >
              <FacebookLogo size={14} weight="fill" className="text-[#1877F2]" /> Facebook
            </button>
            <button
              type="button"
              onClick={shareYoutube}
              data-testid="share-youtube"
              title="Copy the share text and open YouTube Studio — paste into a community post or video description"
              className="px-3 py-2 border border-subtle rounded-sm hover:bg-surface-hover transition flex items-center gap-1.5 text-[11px] font-mono"
            >
              <YoutubeLogo size={14} weight="fill" className="text-neg" /> YouTube
            </button>
          </div>

          <div className="mt-5 pt-5 border-t border-subtle">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">your code</div>
            <div className="font-mono text-2xl tracking-[0.3em] text-primary-fg" data-testid="referral-code">
              {stats.referral_code}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Step n="1" title="Share your link" body="Send your unique link to friends via WhatsApp, X, or Telegram." />
          <Step n="2" title="They sign up" body={`They sign up using your code and instantly get ${stats.welcome_bonus} bonus credits.`} />
          <Step n="3" title="You earn" body={`When they subscribe, you earn ${stats.rewards_per_referral} credits. 50 credits = ₹50 cashback.`} />
        </div>

        {/* Referral history */}
        <div className="border border-subtle bg-surface rounded-sm overflow-hidden mb-8">
          <div className="px-5 py-4 border-b border-subtle">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">/// recent referrals</div>
          </div>
          {referrals.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-fg" data-testid="referrals-empty">
              No referrals yet. Share your link above to get started.
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-12 px-5 py-2.5 border-b border-subtle bg-app/40 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                <div className="col-span-6">Friend</div>
                <div className="col-span-3">Status</div>
                <div className="col-span-3 text-right">Reward</div>
              </div>
              {referrals.map((r) => (
                <div key={`${r.referee_email_masked}-${r.created_at}`} className="grid grid-cols-12 px-5 py-3 border-b border-subtle last:border-b-0 text-sm" data-testid={`referral-row-${r.status}`}>
                  <div className="col-span-6 font-mono text-primary-fg truncate">{r.referee_email_masked}</div>
                  <div className="col-span-3">
                    <span className={
                      "px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border " +
                      (r.status === "completed"
                        ? "bg-pos-soft text-pos border-pos-soft"
                        : "bg-mod-soft text-mod border-mod-soft")
                    }>
                      {r.status === "completed" ? "✓ paid" : "pending"}
                    </span>
                  </div>
                  <div className={"col-span-3 text-right font-mono " + (r.status === "completed" ? "text-pos" : "text-muted-fg")}>
                    +{r.credits_awarded_referrer || stats.rewards_per_referral} cr
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Redeem CTA */}
        <div className="border border-subtle bg-surface rounded-sm p-5 md:p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-mono text-lg text-primary-fg">Ready to cash out?</div>
            <div className="text-sm text-secondary-fg mt-0.5">
              Minimum {stats.min_redemption_credits} credits to redeem. You have <b className="text-pos">{fmt(balance)} credits</b>.
            </div>
          </div>
          <RedeemButton balance={balance} stats={stats} onSuccess={load} />
        </div>

        {/* Terms */}
        <div className="border border-subtle bg-surface rounded-sm p-5 md:p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg mb-3">/// terms</div>
          <ul className="space-y-2 text-sm text-secondary-fg">
            <li className="flex gap-2"><span className="text-mod">•</span> Referral reward is unlocked only after your friend completes a paid subscription.</li>
            <li className="flex gap-2"><span className="text-mod">•</span> Credits are credited within 24 hours of successful payment verification.</li>
            <li className="flex gap-2"><span className="text-mod">•</span> Self-referrals or fraudulent activity will lead to disqualification and credit clawback.</li>
            <li className="flex gap-2"><span className="text-mod">•</span> One referral reward per unique email. We reserve the right to detect duplicate accounts.</li>
            <li className="flex gap-2"><span className="text-mod">•</span> Credits can be redeemed as UPI/bank cashback (₹50 per 50 credits) or applied to future subscriptions.</li>
            <li className="flex gap-2"><span className="text-mod">•</span> Cashback requests are processed manually within 5-7 business days.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, accent = "text-primary-fg", testid }) {
  return (
    <div className="border border-subtle bg-surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`font-mono text-xl tabular-nums ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">{sub}</div>}
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="border border-subtle bg-surface rounded-sm p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-mod">step {n}</div>
      <div className="font-mono text-base text-primary-fg mt-1">{title}</div>
      <div className="text-sm text-secondary-fg mt-1.5">{body}</div>
    </div>
  );
}

function RedeemButton({ balance, stats, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(stats.min_redemption_credits);
  const [method, setMethod] = useState("upi");
  const [upi, setUpi] = useState("");
  const [bank, setBank] = useState({ account: "", ifsc: "", holder: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const eligible = balance >= stats.min_redemption_credits;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (credits > balance) { setErr("Not enough credits"); return; }
    if (credits < stats.min_redemption_credits) { setErr(`Minimum ${stats.min_redemption_credits} credits`); return; }
    if (method === "upi" && !upi.trim()) { setErr("UPI ID required"); return; }
    if (method === "bank" && (!bank.account || !bank.ifsc || !bank.holder)) { setErr("All bank details required"); return; }
    setLoading(true);
    try {
      const payload = { credits, payout_method: method };
      if (method === "upi") payload.upi_id = upi.trim();
      if (method === "bank") {
        payload.bank_account = bank.account.trim();
        payload.bank_ifsc = bank.ifsc.trim();
        payload.bank_holder = bank.holder.trim();
      }
      await api.post("/wallet/redeem", payload);
      setDone(true);
      onSuccess?.();
      setTimeout(() => { setOpen(false); setDone(false); }, 2400);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to redeem");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!eligible}
        data-testid="redeem-btn"
        className={
          "px-5 py-3 rounded-sm font-mono text-[10px] uppercase tracking-widest transition flex items-center gap-2 " +
          (eligible
            ? "hover:opacity-90"
            : "opacity-50 cursor-not-allowed")
        }
        style={eligible ? { background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" } : { background: "hsl(var(--surface-hover))", color: "hsl(var(--text-muted))" }}
      >
        <ShareNetwork size={12} weight="bold" /> {eligible ? "Redeem cashback" : `Need ${stats.min_redemption_credits - balance} more credits`}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="redeem-modal">
          <div className="w-full max-w-md bg-surface border border-subtle rounded-sm p-6" onClick={(e) => e.stopPropagation()}>
            {done ? (
              <div className="text-center py-6" data-testid="redeem-success">
                <CheckCircle size={48} weight="fill" className="text-pos mx-auto mb-3" />
                <div className="font-mono text-xl text-primary-fg mb-1">Request submitted!</div>
                <div className="text-sm text-secondary-fg">We'll process this within 5-7 business days.</div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-mod mb-4">/// redeem credits</div>

                <label className="block mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">Credits to redeem (min {stats.min_redemption_credits})</div>
                  <input
                    type="number"
                    min={stats.min_redemption_credits}
                    max={balance}
                    step={stats.min_redemption_credits}
                    value={credits}
                    onChange={(e) => setCredits(Number(e.target.value))}
                    data-testid="redeem-credits-input"
                    className="w-full px-3 py-2 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm tabular-nums focus:border-focus outline-none"
                  />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mt-1">≈ ₹{fmt(Math.floor(credits / stats.credits_per_rupee))} cashback</div>
                </label>

                <div className="mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">Payout method</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { k: "upi", label: "UPI" },
                      { k: "bank", label: "Bank" },
                      { k: "subscription_discount", label: "Subscription" },
                    ].map((m) => (
                      <button
                        key={m.k}
                        type="button"
                        onClick={() => setMethod(m.k)}
                        className={
                          "px-3 py-2 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                          (method === m.k
                            ? "bg-pos-soft border-pos-soft text-pos"
                            : "border-subtle hover:bg-surface-hover text-secondary-fg")
                        }
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {method === "upi" && (
                  <label className="block mb-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">UPI ID</div>
                    <input
                      value={upi}
                      onChange={(e) => setUpi(e.target.value)}
                      placeholder="yourname@paytm"
                      data-testid="redeem-upi-input"
                      className="w-full px-3 py-2 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm focus:border-focus outline-none"
                    />
                  </label>
                )}
                {method === "bank" && (
                  <div className="space-y-2 mb-3">
                    <input
                      value={bank.account}
                      onChange={(e) => setBank({ ...bank, account: e.target.value })}
                      placeholder="Account number"
                      data-testid="redeem-bank-account"
                      className="w-full px-3 py-2 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm focus:border-focus outline-none"
                    />
                    <input
                      value={bank.ifsc}
                      onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })}
                      placeholder="IFSC"
                      data-testid="redeem-bank-ifsc"
                      className="w-full px-3 py-2 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm focus:border-focus outline-none"
                    />
                    <input
                      value={bank.holder}
                      onChange={(e) => setBank({ ...bank, holder: e.target.value })}
                      placeholder="Account holder name"
                      data-testid="redeem-bank-holder"
                      className="w-full px-3 py-2 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm focus:border-focus outline-none"
                    />
                  </div>
                )}
                {method === "subscription_discount" && (
                  <div className="text-xs text-secondary-fg mb-3 p-3 border border-subtle rounded-sm bg-app">
                    Credits will apply automatically to your next subscription renewal.
                  </div>
                )}

                {err && <div className="text-xs text-neg mb-3" data-testid="redeem-error">{err}</div>}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 px-4 py-2 border border-subtle rounded-sm font-mono text-[10px] uppercase tracking-widest hover:bg-surface-hover transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="redeem-submit"
                    className="flex-1 px-4 py-2 rounded-sm font-mono text-[10px] uppercase tracking-widest transition hover:opacity-90"
                    style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
                  >
                    {loading ? "Submitting…" : "Confirm"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
